import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, API_URL } from '../api';

function Archive() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [archiveResult, setArchiveResult] = useState(null);
  const [form, setForm] = useState({
    from_date: '',
    to_date: '',
    note: '',
    confirm_text: '',
    keep_last: 5
  });

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch('/archive/summary');
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل جلب ملخص الأرشفة');
        return;
      }
      setSummary(data.summary);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب ملخص الأرشفة');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((old) => ({ ...old, [name]: value }));
  };

  const createMasrafiArchive = async () => {
    const ok = window.confirm('سيتم إنشاء ملف أرشيف لوصولات مصرفي حسب الفترة المحددة. هل تريد المتابعة؟');
    if (!ok) return;

    try {
      setWorking('archive');
      setError('');
      setSuccess('');
      setArchiveResult(null);

      const response = await apiFetch('/archive/masrafi-receipts', {
        method: 'POST',
        body: JSON.stringify({
          from_date: form.from_date || null,
          to_date: form.to_date || null,
          note: form.note || ''
        })
      });

      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل إنشاء الأرشيف');
        return;
      }

      setArchiveResult(data);
      setSuccess(data.message || 'تم إنشاء الأرشيف بنجاح');
      await fetchSummary();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء إنشاء الأرشيف');
    } finally {
      setWorking('');
    }
  };

  const cleanupMasrafiReceipts = async () => {
    if (form.confirm_text.trim() !== 'حذف الوصولات المؤرشفة') {
      setError('للتأكيد اكتب: حذف الوصولات المؤرشفة');
      return;
    }

    const ok = window.confirm('تحذير: سيتم حذف بيانات الوصولات المؤرشفة من قاعدة البيانات فقط بعد حفظها في الأرشيف. هل أنت متأكد؟');
    if (!ok) return;

    try {
      setWorking('cleanup-receipts');
      setError('');
      setSuccess('');

      const response = await apiFetch('/archive/cleanup-masrafi-receipts', {
        method: 'POST',
        body: JSON.stringify({
          from_date: form.from_date || null,
          to_date: form.to_date || null,
          confirm_text: form.confirm_text
        })
      });

      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل تنظيف الوصولات المؤرشفة');
        return;
      }

      setSuccess(`${data.message || 'تم التنظيف'} — تقدير المساحة: ${formatNumber(data.estimated_freed_mb)} MB`);
      await fetchSummary();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تنظيف الوصولات');
    } finally {
      setWorking('');
    }
  };

  const cleanupBackups = async () => {
    const ok = window.confirm(`سيتم الإبقاء على آخر ${form.keep_last || 5} ملفات Backup وحذف الأقدم. هل تريد المتابعة؟`);
    if (!ok) return;

    try {
      setWorking('cleanup-backups');
      setError('');
      setSuccess('');

      const response = await apiFetch('/archive/cleanup-backups', {
        method: 'POST',
        body: JSON.stringify({ keep_last: Number(form.keep_last || 5) })
      });

      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل تنظيف النسخ القديمة');
        return;
      }

      setSuccess(`${data.message || 'تم التنظيف'} — تم تحرير ${formatNumber(data.freed_mb)} MB`);
      await fetchSummary();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تنظيف النسخ الاحتياطية');
    } finally {
      setWorking('');
    }
  };

  const downloadUrl = (fileName) => {
    const token = localStorage.getItem('eshara_token') || '';
    return `${API_URL}/archive/download/${encodeURIComponent(fileName)}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  };

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-xl font-bold text-gray-700">جاري تحميل مركز الأرشفة...</div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="bg-white rounded-3xl shadow p-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-black text-gray-900">مركز الأرشفة وتوفير المساحة</h1>
            <p className="text-sm text-gray-500 mt-1">
              أنشئ أرشيف للوصولات والبيانات القديمة ثم نظّف الملفات المؤرشفة بأمان.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate('/')} className="px-4 py-2 rounded-xl bg-gray-100 font-bold">
              الرئيسية
            </button>
            <button onClick={fetchSummary} className="px-4 py-2 rounded-xl bg-orange-500 text-white font-bold">
              تحديث
            </button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 font-bold">{error}</div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4 font-bold">{success}</div>}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow p-4">
            <div className="text-gray-500 text-sm">عدد العمليات</div>
            <div className="text-2xl font-black">{formatNumber(summary?.transactions_count)}</div>
          </div>
          <div className="bg-white rounded-2xl shadow p-4">
            <div className="text-gray-500 text-sm">طلبات مصرفي</div>
            <div className="text-2xl font-black">{formatNumber(summary?.masrafi?.total_requests)}</div>
          </div>
          <div className="bg-white rounded-2xl shadow p-4">
            <div className="text-gray-500 text-sm">وصولات داخل قاعدة البيانات</div>
            <div className="text-2xl font-black">{formatNumber(summary?.masrafi?.local_receipts)}</div>
            <div className="text-xs text-gray-500 mt-1">{formatNumber(summary?.masrafi?.local_receipts_mb)} MB تقريباً</div>
          </div>
          <div className="bg-white rounded-2xl shadow p-4">
            <div className="text-gray-500 text-sm">ملفات Backup</div>
            <div className="text-2xl font-black">{formatNumber(summary?.backups?.files_count)}</div>
            <div className="text-xs text-gray-500 mt-1">{formatNumber(summary?.backups?.total_mb)} MB تقريباً</div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow p-5 space-y-4">
          <h2 className="text-xl font-black">1) إنشاء أرشيف وصولات مصرفي</h2>
          <p className="text-sm text-gray-600">
            هذه الخطوة تنشئ ملف JSON يحتوي على طلبات مصرفي والوصولات الموجودة داخل قاعدة البيانات حسب الفترة المختارة.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="space-y-1">
              <span className="font-bold text-sm">من تاريخ</span>
              <input name="from_date" type="date" value={form.from_date} onChange={handleChange} className="w-full border rounded-xl p-3" />
            </label>
            <label className="space-y-1">
              <span className="font-bold text-sm">إلى تاريخ</span>
              <input name="to_date" type="date" value={form.to_date} onChange={handleChange} className="w-full border rounded-xl p-3" />
            </label>
            <label className="space-y-1">
              <span className="font-bold text-sm">ملاحظة</span>
              <input name="note" value={form.note} onChange={handleChange} placeholder="مثلاً: أرشيف شهر يناير" className="w-full border rounded-xl p-3" />
            </label>
          </div>

          <button
            onClick={createMasrafiArchive}
            disabled={working === 'archive'}
            className="px-5 py-3 rounded-2xl bg-black text-white font-black disabled:opacity-50"
          >
            {working === 'archive' ? 'جاري إنشاء الأرشيف...' : 'إنشاء أرشيف وصولات مصرفي'}
          </button>

          {archiveResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-2">
              <div className="font-black text-blue-900">تم إنشاء الملف: {archiveResult.file_name}</div>
              <div className="text-sm text-blue-800">عدد الطلبات: {formatNumber(archiveResult.records_count)} — الحجم التقريبي: {formatNumber(archiveResult.local_receipts_mb)} MB</div>
              <a
                href={downloadUrl(archiveResult.file_name)}
                className="inline-block px-4 py-2 rounded-xl bg-blue-600 text-white font-bold"
                target="_blank"
                rel="noreferrer"
              >
                تحميل ملف الأرشيف
              </a>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow p-5 space-y-4">
          <h2 className="text-xl font-black">2) تنظيف وصولات مصرفي المؤرشفة</h2>
          <p className="text-sm text-gray-600">
            لا تستخدم هذه الخطوة إلا بعد إنشاء الأرشيف وتحميله. سيتم حذف بيانات الصورة من قاعدة البيانات للطلبات المكتملة/المرفوضة/الملغية فقط.
          </p>

          <label className="space-y-1 block">
            <span className="font-bold text-sm">اكتب للتأكيد: حذف الوصولات المؤرشفة</span>
            <input
              name="confirm_text"
              value={form.confirm_text}
              onChange={handleChange}
              placeholder="حذف الوصولات المؤرشفة"
              className="w-full border rounded-xl p-3"
            />
          </label>

          <button
            onClick={cleanupMasrafiReceipts}
            disabled={working === 'cleanup-receipts'}
            className="px-5 py-3 rounded-2xl bg-red-600 text-white font-black disabled:opacity-50"
          >
            {working === 'cleanup-receipts' ? 'جاري التنظيف...' : 'تنظيف الوصولات المؤرشفة'}
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow p-5 space-y-4">
          <h2 className="text-xl font-black">3) حذف ملفات Backup القديمة</h2>
          <p className="text-sm text-gray-600">
            يحافظ النظام على آخر عدد تحدده من ملفات النسخ الاحتياطية ويحذف الأقدم لتوفير المساحة.
          </p>

          <label className="space-y-1 block max-w-xs">
            <span className="font-bold text-sm">عدد آخر ملفات نحتفظ بها</span>
            <input
              name="keep_last"
              type="number"
              min="1"
              value={form.keep_last}
              onChange={handleChange}
              className="w-full border rounded-xl p-3"
            />
          </label>

          <button
            onClick={cleanupBackups}
            disabled={working === 'cleanup-backups'}
            className="px-5 py-3 rounded-2xl bg-gray-900 text-white font-black disabled:opacity-50"
          >
            {working === 'cleanup-backups' ? 'جاري حذف النسخ القديمة...' : 'تنظيف ملفات Backup القديمة'}
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow p-5">
          <h2 className="text-xl font-black mb-3">آخر ملفات النسخ والأرشيف</h2>
          <div className="space-y-2">
            {(summary?.backups?.latest_files || []).length === 0 ? (
              <div className="text-gray-500">لا توجد ملفات حالياً.</div>
            ) : (
              summary.backups.latest_files.map((file) => (
                <div key={file.relative_path} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border rounded-2xl p-3">
                  <div>
                    <div className="font-bold">{file.file_name}</div>
                    <div className="text-xs text-gray-500">{formatNumber(file.size_mb)} MB</div>
                  </div>
                  <a
                    href={downloadUrl(file.file_name)}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl bg-gray-100 font-bold text-center"
                  >
                    تحميل
                  </a>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-3xl p-5 text-sm leading-7">
          <b>ملاحظة مهمة:</b> أكثر شيء يوفر مساحة هو تنظيف صور الوصولات والنسخ الاحتياطية القديمة.
          سجلات العمليات النصية غالباً حجمها صغير، لذلك لا نحذفها إلا عند الحاجة وبعد وجود Backup.
        </div>
      </div>
    </div>
  );
}

export default Archive;
